import { store } from '@/appContext';
import composeGuards, { ALLOW } from '@/utilities/routeGuards';
import eventBus from '@/utilities/EventBus';
import useRegistrationDealer from '@/composables/useRegistrationDealer';
import { pathNames, routeNames } from './acvRoute';

const isDevelopment = import.meta.env.VITE_ENV_NAME === 'development';

const requireRegistrationToken = (to, from, next) => {
  if (isDevelopment || store.getters.hasRegistrationToken) {
    next();
  } else {
    next({
      path: pathNames.get('registration_v2'),
      query: to.query,
      hash: to.hash,
      replace: true,
    });
  }
};

const redirectPastVerificationIfComplete = (to, from, next) => {
  if (store.getters.hasRegistrationToken) {
    next({ name: routeNames.get('registrationDealerships_v2'), replace: true });
  } else {
    next();
  }
};

const clearStaleRegistrationToken = (to, from, next) => {
  if (store.getters.hasRegistrationToken) {
    store.dispatch('clearRegistrationToken');
  }
  next();
};

const requireMaskedEmail = (to, from, next) => {
  if (store.getters.maskedEmail) {
    next();
  } else {
    eventBus.$emit('acv::showToastMessage', {
      header: 'Alert.',
      severity: 'warning',
      message: "We couldn't find your registration info. Please try again.",
    });
    next({ name: routeNames.get('registrationForm_v2'), replace: true });
  }
};

const allowIfArrivingFromEmailVerification = (to, from, next) => {
  if (from.name === routeNames.get('registrationEmailVerification_v2')) {
    next(ALLOW);
  } else {
    next();
  }
};

// requireSteppedUpSession and requireSelectedDealer already run earlier in this guard chain,
// so a login coming through the app-wide /login (e.g. after a "forgot password" detour through
// /forgot and /password, which redirect back to generic /login rather than back into the
// registration flow) is just as valid as one coming straight from RegistrationAcvLoginV2.
const allowIfArrivingFromLoginFlow = (to, from, next) => {
  const loginRouteNames = [routeNames.get('registrationAcvLogin_v2'), routeNames.get('login')];

  if (loginRouteNames.includes(from.name)) {
    next(ALLOW);
  } else {
    next();
  }
};

const redirectUnverifiedToRegistrationForm = (to, from, next) => {
  next({ name: routeNames.get('registrationForm_v2'), replace: true });
};

const requireSelectedDealer = (to, from, next) => {
  if (isDevelopment || store.getters.selectedDealer) {
    next();
  } else {
    next({ name: routeNames.get('registrationDealerships_v2') });
  }
};

// Guards against a dealer form visit that skipped RegistrationAcvLogin's step-up. A registration
// token can exist and be scoped to a dealer without the user ever having proven their Marketplace
// login is affiliated with it - hasSteppedUp only flips true once that step-up actually succeeds.
const requireSteppedUpSession = (to, from, next) => {
  if (isDevelopment || store.getters.hasSteppedUp) {
    next();
  } else {
    next({ name: routeNames.get('registrationAcvLogin_v2') });
  }
};

const resetSelectedDealer = (to, from, next) => {
  const { selectedDealer, clearSelectedDealer } = useRegistrationDealer();
  if (selectedDealer.value) {
    clearSelectedDealer();
  }
  next();
};

const defaultMeta = {
  allowUnauthorized: true,
  showFooter: false,
};

const isEnabledForV2 = import.meta.env.VITE_IS_NEW_REGISTRATION_FLOW_ENABLED === 'true';

const redirectToV2IfEnabled = (to, from, next) => {
  if (isEnabledForV2) {
    next({
      path: pathNames.get('registration_v2'),
      query: to.query,
      hash: to.hash,
      replace: true,
    });
  } else {
    next();
  }
};

export const registrationRoute = {
  path: pathNames.get('registration'),
  component: () => import('@/views/AuctionAccess/AuctionAccess.vue'),
  title: 'ACV Registration with Auction Access',
  meta: {
    ...defaultMeta,
    showHeader: false,
  },
  children: [
    {
      name: 'Auction Access Landing',
      path: pathNames.get('emptyPath'),
      component: () => import('@/components/AuctionAccess/AuctionAccessLanding.vue'),
      meta: {
        ...defaultMeta,
        title: 'What is AuctionAccess?',
        showHeader: true,
      },
      beforeEnter: redirectToV2IfEnabled,
    },
    {
      name: routeNames.get('registrationForm'),
      path: pathNames.get('registrationForm'),
      component: () => import('@/components/AuctionAccess/AuctionAccessForm.vue'),
      meta: {
        ...defaultMeta,
        title: 'Provide your AuctionAccess Information',
        showHeader: true,
      },
      beforeEnter: redirectToV2IfEnabled,
    },
    {
      path: '/auction-access-form',
      redirect: pathNames.get('registrationForm'),
    },
    {
      name: routeNames.get('registrationDealerships'),
      path: pathNames.get('registrationDealerships'),
      component: () => import('@/components/AuctionAccess/AuctionAccessDealerships.vue'),
      meta: {
        ...defaultMeta,
        title: 'AuctionAccess Dealership Select',
        showHeader: true,
      },
      beforeEnter: redirectToV2IfEnabled,
    },
    {
      path: '/auction-access-dealerships',
      redirect: pathNames.get('registrationDealerships'),
    },
  ],
};

const redirectToV1IfDisabled = (redirectPath) => (to, from, next) => {
  if (isEnabledForV2) {
    next();
  } else {
    next({
      path: redirectPath,
      query: to.query,
      hash: to.hash,
    });
  }
};

const defaultMetaV2 = {
  allowUnauthorized: true,
  showFooter: false,
  showHeader: false,
  isRegistrationV2: true,
  showRegistrationNavHeader: true,
  showRegistrationFormHeader: false,
  offsetFixedHeader: false,
  useSplitHero: false,
};

const registrationLanding = {
  name: routeNames.get('registration_v2'),
  path: pathNames.get('emptyPath'),
  component: () => import('@/components/Registration/RegistrationLanding.vue'),
  meta: {
    ...defaultMetaV2,
    useSplitHero: true,
  },
  beforeEnter: composeGuards(
    redirectToV1IfDisabled(pathNames.get('registration')),
    clearStaleRegistrationToken,
  ),
};

const registrationForm = {
  name: routeNames.get('registrationForm_v2'),
  path: pathNames.get('registrationForm_v2'),
  component: () => import('@/components/Registration/RegistrationForm.vue'),
  meta: {
    ...defaultMetaV2,
    useSplitHero: true,
  },
  beforeEnter: composeGuards(
    redirectToV1IfDisabled(pathNames.get('registrationForm')),
    clearStaleRegistrationToken,
  ),
};

const biddingStartsHere = {
  title: 'Your bidding starts here',
  subtitle: 'Register today to start bidding and winning used car auctions',
};

const registrationEmailVerification = {
  name: routeNames.get('registrationEmailVerification_v2'),
  path: pathNames.get('registrationEmailVerification_v2'),
  component: () => import('@/components/Registration/RegistrationEmailVerification/RegistrationEmailVerification.vue'),
  meta: {
    ...defaultMetaV2,
    ...biddingStartsHere,
    heroTitle: biddingStartsHere.title,
    offsetFixedHeader: true,
    useSplitHero: true,
  },
  beforeEnter: composeGuards(
    redirectToV1IfDisabled(pathNames.get('registrationForm')),
    redirectPastVerificationIfComplete,
    requireMaskedEmail,
  ),
};

const registrationIdentityVerified = {
  name: routeNames.get('registrationIdentityVerified_v2'),
  path: pathNames.get('registrationSuccess_v2'),
  component: () => import('@/components/Registration/RegistrationSuccessConfirmation.vue'),
  meta: {
    ...defaultMetaV2,
    title: 'Identity Verified',
    heroTitle: biddingStartsHere.title,
    pushDestination: routeNames.get('registrationDealerships_v2'),
    offsetFixedHeader: true,
    useSplitHero: true,
  },
  beforeEnter: composeGuards(
    redirectToV1IfDisabled(pathNames.get('registrationForm')),
    allowIfArrivingFromEmailVerification,
    redirectPastVerificationIfComplete,
    redirectUnverifiedToRegistrationForm,
  ),
};

const registrationDealerships = {
  name: routeNames.get('registrationDealerships_v2'),
  path: pathNames.get('registrationDealerships_v2'),
  component: () => import('@/components/Registration/RegistrationDealerships/RegistrationDealerships.vue'),
  meta: {
    ...defaultMetaV2,
    title: 'AuctionACCESS Dealership Select',
    offsetFixedHeader: true,
  },
  beforeEnter: composeGuards(
    redirectToV1IfDisabled(pathNames.get('registrationDealerships')),
    requireRegistrationToken,
    resetSelectedDealer,
  ),
};

const registrationDealerForm = {
  name: routeNames.get('registrationDealerForm_v2'),
  path: pathNames.get('registrationDealerForm_v2'),
  component: () => import('@/components/Registration/RegistrationDealerForm/RegistrationDealerFormWizard.vue'),
  meta: {
    ...defaultMetaV2,
    title: 'Dealer Registration Form',
    showRegistrationFormHeader: true,
    offsetFixedHeader: true,
  },
  beforeEnter: composeGuards(
    requireRegistrationToken,
    requireSteppedUpSession,
    requireSelectedDealer,
  ),
};

const registrationAcvLogin = {
  name: routeNames.get('registrationAcvLogin_v2'),
  path: pathNames.get('registrationAcvLogin_v2'),
  component: () => import('@/components/Registration/RegistrationAcvLogin/RegistrationAcvLogin.vue'),
  meta: {
    ...defaultMetaV2,
    title: 'YOUR BIDDING STARTS HERE',
    subtitle: 'Register today to start bidding and winning used car auctions',
    heroTitle: biddingStartsHere.title,
    useSplitHero: true,
  },
  beforeEnter: composeGuards(
    requireRegistrationToken,
    requireSelectedDealer,
  ),
};

const registrationAcvLoginSuccess = {
  name: routeNames.get('registrationAcvLoginSuccess_v2'),
  path: pathNames.get('registrationSuccess_v2'),
  component: () => import('@/components/Registration/RegistrationSuccessConfirmation.vue'),
  meta: {
    ...defaultMetaV2,
    title: 'Logging you in',
    heroTitle: biddingStartsHere.title,
    pushDestination: routeNames.get('registrationDealerForm_v2'),
    offsetFixedHeader: true,
    useSplitHero: true,
  },
  beforeEnter: composeGuards(
    redirectToV1IfDisabled(pathNames.get('registrationForm')),
    requireSteppedUpSession,
    requireSelectedDealer,
    allowIfArrivingFromLoginFlow,
    redirectUnverifiedToRegistrationForm,
  ),
};

const registrationAcvAccountCreationSuccess = {
  name: routeNames.get('registrationAcvAccountCreationSuccess_v2'),
  path: pathNames.get('registrationSuccess_v2'),
  component: () => import('@/components/Registration/RegistrationSuccessConfirmation.vue'),
  meta: {
    ...defaultMetaV2,
    title: 'Account Created!',
    subtitle: 'Your ACV profile has been successfully created.',
    heroTitle: biddingStartsHere.title,
    pushDestination: routeNames.get('registrationDealerships_v2'),
    offsetFixedHeader: true,
    useSplitHero: true,
  },
  beforeEnter: composeGuards(
    redirectToV1IfDisabled(pathNames.get('registrationForm')),
    redirectUnverifiedToRegistrationForm,
  ),
};

const registrationAcvAccountCreation = {
  name: routeNames.get('registrationAcvAccountCreation_v2'),
  path: pathNames.get('registrationAcvAccountCreation_v2'),
  component: () => import('@/components/Registration/RegistrationAcvAccountCreation/RegistrationAcvAccountCreation.vue'),
  meta: {
    ...defaultMetaV2,
    heroTitle: biddingStartsHere.title,
    useSplitHero: true,
  },
  beforeEnter: composeGuards(
    requireRegistrationToken,
    allowIfArrivingFromEmailVerification,
    redirectUnverifiedToRegistrationForm,
  ),
};

const registrationLayoutSwitcher = () => import('@/views/Registration/RegistrationLayoutSwitcher.vue');

export const registrationV2Route = {
  path: pathNames.get('registration_v2'),
  title: 'ACV Registration with AuctionACCESS Version 2',
  component: registrationLayoutSwitcher,
  meta: {
    ...defaultMetaV2,
  },
  children: [
    registrationLanding,
    registrationForm,
    registrationEmailVerification,
    registrationIdentityVerified,
    registrationAcvAccountCreation,
    registrationAcvAccountCreationSuccess,
    registrationDealerships,
    registrationAcvLogin,
    registrationAcvLoginSuccess,
    registrationDealerForm,
  ],
};
